import { CouponInput, CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseInputDto } from "@modules/common/pauseObject/gateway/pauseApiDto"
import { word } from "@modules/core/utils/i18n"
import * as Yup from "yup"
import { z } from "zod"

export enum DurationType {
  MONTHLY = "MONTHLY",
  UNLIMITED = "UNLIMITED",
}
// DELETE SUBSCRIPTION VALIDATION
export const deleteSubscriptionYupValidation = () =>
  Yup.object({
    cancelAtPeriodEnd: Yup.boolean().required(word("global.form.required")),
    endPeriodInMonths: Yup.number().min(0, word("delete.subscription.yupValidation.errorMinimum")).optional(),
  }).test((value, context) => {
    if (
      value.cancelAtPeriodEnd === true &&
      (value.endPeriodInMonths === undefined || value.endPeriodInMonths < 0 || value.endPeriodInMonths > 6)
    ) {
      return new Yup.ValidationError(
        word("delete.subscription.yupValidation.errorEndPeriodInMonths"),
        value.endPeriodInMonths,
        context.path,
      )
    }
    return true
  })

// PAUSE VALIDATION
export const pauseYupValidation = () =>
  Yup.object({
    durationType: Yup.mixed<DurationType>().oneOf(Object.values(DurationType)).required(),
    months: Yup.number()
      .nullable()
      .test((value, context) => {
        if (context.parent.durationType == DurationType.UNLIMITED && value !== null) {
          return new Yup.ValidationError(word("pause.subscription.yupValidation.errorUnlimited"), value, context.path)
        }
        if (context.parent.durationType === DurationType.MONTHLY && (value ?? 0) < 1) {
          return new Yup.ValidationError(word("pause.subscription.yupValidation.errorMonths"), value, context.path)
        }
        return true
      }),
  })

export const PauseInputFormikSchema = z
  .object({
    durationType: z.nativeEnum(DurationType),
    months: z.number().nullable(),
  })
  .refine((data) => {
    if (data.durationType === DurationType.MONTHLY && !data.months) {
      return false
    }
    return true
  })

export const convertPauseInputToRequest = (data: PauseInputFormik): PauseInputDto => {
  return {
    durationInMonths: data.durationType === DurationType.MONTHLY ? data.months : null,
    programAccessAllowed: true,
  }
}

// COUPON VALIDATION
export const couponYupValidation = (options: { priceOffer: number }) =>
  Yup.object({
    durationType: Yup.mixed<DurationType>().oneOf(Object.values(DurationType)).required(),
    months: Yup.number()
      .nullable()
      .test((value, context) => {
        if (context.parent.durationType == DurationType.UNLIMITED && value !== null) {
          return new Yup.ValidationError(word("pause.subscription.yupValidation.errorUnlimited"), value, context.path)
        }
        if (context.parent.durationType === DurationType.MONTHLY && (value ?? 0) < 1) {
          return new Yup.ValidationError(word("pause.subscription.yupValidation.errorMonths"), value, context.path)
        }
        return true
      }),
    type: Yup.mixed<CouponType>().oneOf(Object.values(CouponType)).required(),
    value: Yup.number()
      .required(word("coupon.subscription.yupValidation.valueRequired"))
      .min(0, word("coupon.subscription.yupValidation.valueMinimum"))
      .test((value, context) => {
        if (context.parent.type === CouponType.PERCENT && (value > 100 || value <= 0)) {
          return new Yup.ValidationError(
            word("coupon.subscription.yupValidation.valuePercentError"),
            value,
            context.path,
          )
        }

        let priceAfterDiscount = options.priceOffer
        if (context.parent.type === CouponType.PERCENT) {
          priceAfterDiscount = options.priceOffer * (value / 100)
        } else if (context.parent.type === CouponType.DIRECT) {
          priceAfterDiscount = options.priceOffer - value
        }

        if (priceAfterDiscount < 0) {
          return new Yup.ValidationError(
            word("coupon.subscription.yupValidation.priceAfterDiscountError"),
            value,
            context.path,
          )
        }

        return true
      }),
  })

export type CouponInputFormikKeys = "durationType" | "months" | "type" | "value"

export const CouponInputFormikSchema = z
  .object({
    durationType: z.nativeEnum(DurationType),
    months: z.number().nullable(),
    type: z.nativeEnum(CouponType),
    value: z.number().min(1),
  })
  .refine((data) => {
    if (data.durationType === DurationType.MONTHLY && !data.months) {
      return false
    }
    return true
  })
  .refine((data) => {
    if (data.type === CouponType.PERCENT && data.value > 100) {
      return false
    }
    return true
  })

export const convertCouponInputToRequest = (data: CouponInputFormik): CouponInput => {
  return {
    durationInMonths: data.months,
    type: data.type,
    value: data.value,
  }
}

// TYPE EXPORTS
export type PauseInputFormik = z.infer<typeof PauseInputFormikSchema>
export type CouponInputFormik = z.infer<typeof CouponInputFormikSchema>
